import { useEffect, useState } from "react";
import { Fingerprint, Plus, Trash2, Pencil, Check, X, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import {
  addPasskey,
  listPasskeys,
  removePasskey,
  renamePasskey,
  isPasskeySupported,
  isPlatformAuthenticatorAvailable,
  friendlyAuthenticatorName,
} from "./passkeyApi";

const fmtDateTime = (iso) =>
  iso
    ? new Date(iso).toLocaleString("en-IN", {
        day: "numeric",
        month: "short",
        year: "numeric",
        hour: "numeric",
        minute: "2-digit",
      })
    : "Never used";

export default function PasskeySection() {
  const [supported, setSupported] = useState(false);
  const [platformAvail, setPlatformAvail] = useState(false);
  const [items, setItems] = useState(null);
  const [busy, setBusy] = useState(false);
  const [renamingId, setRenamingId] = useState(null);
  const [renameDraft, setRenameDraft] = useState("");

  const refresh = async () => {
    try {
      const rows = await listPasskeys();
      setItems(rows);
    } catch {
      setItems([]);
    }
  };

  useEffect(() => {
    setSupported(isPasskeySupported());
    isPlatformAuthenticatorAvailable().then(setPlatformAvail);
    refresh();
  }, []);

  const guessNickname = () => {
    const ua = navigator.userAgent || "";
    if (/iPhone/.test(ua)) return "iPhone";
    if (/iPad/.test(ua)) return "iPad";
    if (/Mac/.test(ua)) return "Mac";
    if (/Android/.test(ua)) return "Android phone";
    if (/Windows/.test(ua)) return "Windows PC";
    if (/CrOS/.test(ua)) return "Chromebook";
    return "This device";
  };

  const onAdd = async () => {
    setBusy(true);
    try {
      const nickname = window.prompt("Name this passkey", guessNickname()) || guessNickname();
      await addPasskey(nickname);
      toast.success("Passkey added — you can sign in with Face ID now");
      await refresh();
    } catch (e) {
      const name = e?.name || "";
      const detail = e?.response?.data?.detail || "";
      if (name === "NotAllowedError") {
        toast.message("Passkey setup cancelled");
      } else if (name === "InvalidStateError" || detail.includes("already")) {
        toast.info("This device already has a passkey registered");
      } else {
        toast.error(detail || "Could not add passkey");
      }
    } finally {
      setBusy(false);
    }
  };

  const onRename = async (credId) => {
    const nickname = (renameDraft || "").trim();
    if (!nickname) {
      setRenamingId(null);
      return;
    }
    try {
      await renamePasskey(credId, nickname);
      toast.success("Renamed");
      setRenamingId(null);
      setRenameDraft("");
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Rename failed");
    }
  };

  const onRemove = async (credId, nickname) => {
    if (!window.confirm(`Remove "${nickname}" passkey? You'll need your password on this device.`)) return;
    try {
      await removePasskey(credId);
      toast.success("Passkey removed");
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Remove failed");
    }
  };

  if (!supported) {
    return (
      <div>
        <div className="text-xs font-semibold uppercase tracking-widest text-neutral-500 mb-3">
          Sign in with Face ID / fingerprint
        </div>
        <div className="rounded-xl bg-neutral-900 border border-neutral-800 px-4 py-3 text-[12px] text-neutral-400">
          This browser doesn't support passkeys. Open the portal in Safari (iPhone/iPad/Mac) or the latest Chrome, Edge, or Firefox to enable Face ID / fingerprint sign-in.
        </div>
      </div>
    );
  }

  return (
    <div data-testid="passkey-section">
      <div className="flex items-center justify-between mb-3">
        <div className="text-xs font-semibold uppercase tracking-widest text-neutral-500">
          Sign in with Face ID / fingerprint
        </div>
        <button
          onClick={onAdd}
          disabled={busy}
          data-testid="passkey-add-btn"
          className="inline-flex items-center gap-2 rounded-full border border-orange-500/30 bg-orange-500/10 hover:bg-orange-500/20 text-orange-200 px-3 py-1.5 text-xs font-medium disabled:opacity-60 disabled:cursor-not-allowed"
        >
          <Plus className="h-3.5 w-3.5" /> {busy ? "Setting up…" : "Add passkey"}
        </button>
      </div>

      <div className="rounded-xl bg-neutral-900 border border-neutral-800 p-4">
        <div className="flex items-start gap-3 mb-3 pb-3 border-b border-neutral-800">
          <div className="h-9 w-9 rounded-lg bg-orange-500/15 border border-orange-500/30 flex items-center justify-center shrink-0">
            <Fingerprint className="h-4 w-4 text-orange-300" />
          </div>
          <div className="text-[12px] text-neutral-300 leading-relaxed">
            Skip typing your password. Set up Face ID, Touch ID, or your phone's fingerprint once and every future sign-in from that device is a single tap.
            {!platformAvail && (
              <div className="mt-1.5 text-[11px] text-neutral-500">
                Your device doesn't advertise a biometric sensor — you can still add a passkey using a phone or security key.
              </div>
            )}
          </div>
        </div>

        {items === null ? (
          <div className="text-[12px] text-neutral-500 italic">Loading passkeys…</div>
        ) : items.length === 0 ? (
          <div className="text-[12px] text-neutral-400 flex items-center gap-2">
            <ShieldCheck className="h-3.5 w-3.5 text-neutral-500" />
            No passkeys yet. Tap <b className="text-neutral-200">Add passkey</b> to set one up on this device.
          </div>
        ) : (
          <ul className="space-y-2">
            {items.map((p) => {
              const provider = friendlyAuthenticatorName(p.aaguid);
              const isRenaming = renamingId === p.credential_id;
              return (
                <li
                  key={p.credential_id}
                  data-testid={`passkey-row-${p.credential_id.slice(0, 12)}`}
                  className="rounded-lg bg-neutral-950 border border-neutral-800 px-3 py-2.5 flex items-center gap-3"
                >
                  <Fingerprint className="h-4 w-4 text-neutral-500 shrink-0" />
                  <div className="flex-1 min-w-0">
                    {isRenaming ? (
                      <input
                        autoFocus
                        value={renameDraft}
                        onChange={(e) => setRenameDraft(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") onRename(p.credential_id);
                          if (e.key === "Escape") setRenamingId(null);
                        }}
                        data-testid={`passkey-rename-input-${p.credential_id.slice(0, 12)}`}
                        className="w-full bg-neutral-900 border border-neutral-700 rounded px-2 py-1 text-sm outline-none focus:border-orange-500"
                      />
                    ) : (
                      <>
                        <div className="text-sm font-medium truncate">{p.nickname || "Passkey"}</div>
                        <div className="text-[10px] text-neutral-500 truncate">
                          {provider ? `${provider} · ` : ""}Last used {fmtDateTime(p.last_used_at)}
                        </div>
                      </>
                    )}
                  </div>
                  {isRenaming ? (
                    <>
                      <button
                        onClick={() => onRename(p.credential_id)}
                        data-testid={`passkey-rename-save-${p.credential_id.slice(0, 12)}`}
                        className="h-7 w-7 rounded-full bg-orange-500 hover:bg-orange-400 text-white flex items-center justify-center"
                        aria-label="Save"
                      >
                        <Check className="h-3.5 w-3.5" />
                      </button>
                      <button
                        onClick={() => setRenamingId(null)}
                        className="h-7 w-7 rounded-full bg-neutral-800 hover:bg-neutral-700 text-neutral-300 flex items-center justify-center"
                        aria-label="Cancel"
                      >
                        <X className="h-3.5 w-3.5" />
                      </button>
                    </>
                  ) : (
                    <>
                      <button
                        onClick={() => {
                          setRenamingId(p.credential_id);
                          setRenameDraft(p.nickname || "Passkey");
                        }}
                        data-testid={`passkey-rename-${p.credential_id.slice(0, 12)}`}
                        className="h-7 w-7 rounded-full text-neutral-500 hover:text-neutral-200 hover:bg-neutral-800 flex items-center justify-center"
                        aria-label="Rename"
                      >
                        <Pencil className="h-3.5 w-3.5" />
                      </button>
                      <button
                        onClick={() => onRemove(p.credential_id, p.nickname || "Passkey")}
                        data-testid={`passkey-remove-${p.credential_id.slice(0, 12)}`}
                        className="h-7 w-7 rounded-full text-neutral-500 hover:text-red-400 hover:bg-neutral-800 flex items-center justify-center"
                        aria-label="Remove"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}
